import { IPredictionGroupingDto } from 'api/api';
import moment from 'moment';
import React, { FC, memo, useCallback } from 'react';
import { localized } from 'state/i18n';
import BiLabelContentItem from 'view/shared/components/bi-label-content/bi-label-content-item';

interface Props {
	data: IPredictionGroupingDto[];
}

export const PredictionSummary: FC<Props> = memo(({ data }) => {
	const getNthDayAway = useCallback(
		(grouping: IPredictionGroupingDto, noOfDaysAway: number) =>
			grouping.predictions?.find(
				p => moment(grouping.actualEmptiedDate).diff(p.predictionDate, 'days') === noOfDaysAway
			),
		[]
	);

	const onlyFullWhenEmptied = data.filter(
		pg => pg.fillLevelWhenEmptied === 100 && pg.actualEmptiedDate && pg.predictions
	);

	const overallDiff = onlyFullWhenEmptied.reduce(
		(prev, curr): { diff: number; predictionCount: number } => {
			const absoluteDiff = curr.predictions?.reduce(
				(prevSum, currPred) =>
					prevSum +
					Math.abs(
						currPred?.predictedMinutesUntilFull! -
							moment(curr.actualEmptiedDate).diff(currPred?.predictionDate, 'minutes')
					),
				0
			);
			return {
				diff: prev.diff + (absoluteDiff ?? 0),
				predictionCount: prev.predictionCount + (curr.predictions?.length ?? 0),
			};
		},
		{ diff: 0, predictionCount: 0 }
	);

	const overallDiffDays = overallDiff.diff / (60 * 24) / overallDiff.predictionCount;

	const oneWeekAwayList = onlyFullWhenEmptied.filter(pg => getNthDayAway(pg, 7));

	const oneWeekAwayDiff =
		oneWeekAwayList.reduce((prev, curr) => {
			const oneWeekAgoPrediction = getNthDayAway(curr, 7);
			const absoluteDiff = Math.abs(
				oneWeekAgoPrediction?.predictedMinutesUntilFull! -
					moment(curr.actualEmptiedDate).diff(oneWeekAgoPrediction?.predictionDate, 'minutes')
			);
			return prev + absoluteDiff;
		}, 0) /
		oneWeekAwayList.length /
		(60 * 24);

	return (
		<div>
			<BiLabelContentItem
				defaultClassNames="bi-label-content-item-lg unit-manufacturerer-grid"
				containerClassName="margin-bottom-10px"
				label={localized('OverallPredictionDifference')}
				content={
					Number.isFinite(overallDiffDays) ? `${overallDiffDays.toFixed(2)} ${localized('days')}` : 'N/A'
				}
			/>
			<BiLabelContentItem
				defaultClassNames="bi-label-content-item-lg unit-manufacturerer-grid"
				containerClassName="margin-bottom-10px"
				label={localized('OneWeekPredictionDifference')}
				content={
					Number.isFinite(oneWeekAwayDiff) ? `${oneWeekAwayDiff.toFixed(2)} ${localized('days')}` : 'N/A'
				}
			/>
		</div>
	);
});
